/* eslint-disable react/no-danger, camelcase */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Element } from 'react-scroll';
import { withRouter } from 'react-router';
import { hot } from 'react-hot-loader/root';
import { denormalize } from 'normalizr';
import SVGInline from 'react-svg-inline';
import memoize from 'fast-memoize';
import memoizeOne from 'memoize-one';

import config from 'config/config';

import get from 'lodash/get';

import cond from 'ramda/src/cond';
import propSatisfies from 'ramda/src/propSatisfies';
import gt from 'ramda/src/gt';
import lte from 'ramda/src/lte';

import Schemas from 'redux-logic/schemas';
import denormalizeGamesArr from 'tools/redux/denormalize-games-arr';
import denormalizeGame from 'tools/redux/denormalize-game';
import SimpleIntlMessage from 'app/components/simple-intl-message';
import Breadcrumbs from 'app/ui/breadcrumbs';
import Time from 'app/ui/time';
import Heading from 'app/ui/heading';

import len from 'tools/array/len';
import getSiteUrl from 'tools/get-site-url';

import editIcon from 'assets/icons/edit.svg';
import styleVars from 'styles/vars.json';

import appHelper from 'app/pages/app/app.helper';
import Page from 'app/ui/page';
import Content from 'app/ui/content';
import Tabs from 'app/ui/tabs';
import Tab from 'app/ui/tabs/tab';
import RatingChart from 'app/ui/rating-chart';
import RedditList from 'app/ui/reddit-list';
import ContentPlaceholder from 'app/ui/content-placeholder';
import HiddenLink from 'app/ui/hidden-link';
import { appRatingsType, appSizeType, appLocaleType } from 'app/pages/app/app.types';
import currentUserType from 'app/components/current-user/current-user.types';
import paths from 'config/paths';
import GameButtonsNew from 'app/components/game-buttons-new';
import RenderMounted from 'app/render-props/render-mounted';

import getAppContainerWidth from 'tools/get-app-container-width';
import locationShape from 'tools/prop-types/location-shape';
import intlShape from 'tools/prop-types/intl-shape';

import GamePersons from './game-persons';

import GameHeadBlock from './game/head';
import GameScreenshotsBlock from './game/screenshots';
import GameRatingsBlock from './game/ratings';
import GameOwnersBlock from './game/owners';
import GameAboutBlock from './game/about';
import GameMetaBlock from './game/meta';
import GameSystemRequirementsBlock from './game/system-requirements';
import GameAvailabilityBlock from './game/availability';
import GameSuggestionsBlock from './game/suggestions';
import GameCollectionsBlock from './game/collections';
import GameYoutubeBlock from './game/youtube';
import GameImgurBlock from './game/imgur';
import GameAchievementsBlock from './game/achievements';
import GameTwitchBlock from './game/twitch';
import GameReviewsBlock from './game/reviews';
import GamePostsBlock from './game/posts';
import GameFilesBlock from './game/files';
import GameContributorsBlock from './game/contributors';

import gameType from './game.types';

import { isUgly, AggregateRating } from './game.helper';
import { loadGameSuggestions } from './game.actions';
import { genres as genresType } from '../games/games.types';

import './game.styl';

const getGame = ({ location, game }) => {
  const gameFromLocation = location.state || {};

  return len(game, 'name') === 0 && Object.keys(gameFromLocation).length > 0 ? gameFromLocation : game;
};

const getClipByWidth = cond([
  [propSatisfies(gt(500), 'width'), ({ clips }) => clips['320']],
  [propSatisfies(gt(1000), 'width'), ({ clips }) => clips['640']],
  [propSatisfies(lte(1000), 'width'), ({ clips }) => clips.full],
]);

const connector = (state) => ({
  firstPage: state.app.firstPage,
  ratings: state.app.ratings,
  genres: state.app.genres,
  size: state.app.size,
  locale: state.app.locale,
  isSpider: state.app.request.isSpider,
  currentUser: state.currentUser,
  game: denormalizeGame(state),
  suggestedGames: denormalizeGamesArr(state, 'game.suggestions.results'),
  personsItems: denormalize(state.game.persons.results, Schemas.PERSON_ARRAY, state.entities),
  host: state.app.request.headers.host,
});

const gamePropertyTypes = {
  game: gameType.isRequired,
  ratings: appRatingsType.isRequired,
  genres: genresType.isRequired,
  size: appSizeType.isRequired,
  locale: appLocaleType.isRequired,
  isSpider: PropTypes.bool.isRequired,
  currentUser: currentUserType.isRequired,
  dispatch: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  location: locationShape.isRequired,
  suggestedGames: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  personsItems: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  host: PropTypes.string.isRequired,
  firstPage: PropTypes.bool.isRequired,
};

const videoEnabled = false;

@hot
@injectIntl
@connect(connector)
@withRouter
export default class Game extends Component {
  static propTypes = gamePropertyTypes;

  constructor(props, context) {
    super(props, context);

    this.openViewer = memoize(this.openViewer);
    this.getTabsBackground = memoizeOne(this.getTabsBackground);

    this.state = {
      tab: 'reviews',
      viewerVisible: false,
      additionalDataLoaded: false,
      oldGameId: undefined,
      activeSection: 'about',
    };
  }

  async componentDidMount() {
    if (!this.props.firstPage) {
      window.scrollTo(0, 0);
    }

    import('./game.additional').then(({ gameHeader, gameViewer }) => {
      this.GameHeader = gameHeader;
      this.GameViewer = gameViewer;

      this.setState({
        additionalDataLoaded: true,
      });
    });
  }

  componentDidUpdate(previousProperties) {
    const oldGame = getGame(previousProperties);
    const newGame = getGame(this.props);

    if (oldGame.id && oldGame.id !== newGame.id) {
      window.scrollTo(0, 0);
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (!props.location) {
      return null;
    }

    const propsGame = getGame(props);

    if (state.oldGameId !== propsGame.id) {
      return {
        oldGameId: propsGame.id,
        viewerVisible: false,
      };
    }

    return null;
  }

  setActiveSection = (activeSection) => {
    this.setState({ activeSection });
  };

  closeViewer = () => {
    this.setState({
      viewerVisible: false,
    });
  };

  openViewer = (content, index) => (event) => {
    event.preventDefault();

    this.setState({
      viewerVisible: true,
      viewerContent: content,
      viewerIndex: index,
    });
  };

  showTab = (tab) => {
    this.setState({ tab });
  };

  getTabsBackground = (background_image_additional, visible) => ({
    backgroundImage:
      background_image_additional && visible
        ? `
        radial-gradient(ellipse closest-side at center, transparent, ${styleVars.mainBGColor}),
        url('${background_image_additional.replace('/media/', '/media/resize/1280/-/')}')
      `
        : '',
  });

  loadSuggestedGames = (pageSize) => {
    const { game, dispatch } = this.props;
    const {
      slug,
      suggestions: { next },
    } = game;

    return dispatch(loadGameSuggestions(slug, next, pageSize));
  };

  getGameVideo = (game) => {
    const { host } = this.props;
    const width = getAppContainerWidth();
    const clips = get(game, 'clip.clips');

    if (videoEnabled && clips && appHelper.isStageWebsite(host)) {
      return getClipByWidth({ width, clips });
    }

    return undefined;
  };

  getPageProps = (game) => {
    const {
      seo_title: title,
      seo_description: description,
      seo_keyword: keywords,
      image,
      background_image,
      dominant_color,
      slug,
      persons: { results: persons } = {},
    } = game;

    const video = this.getGameVideo(game);

    return {
      persons,
      helmet: {
        addProjectName: false,
        title,
        description,
        keywords,
        image: background_image,
        canonical: paths.game(slug),
        // ampLink: paths.ampGame(slug),
      },
      className: 'game',
      art: {
        image: {
          path: image || background_image,
          color: `#${dominant_color}`,
        },
        height: video ? '56.25vw' : '500px',
        colored: true,
        ugly: isUgly(game),
        video,
      },

      sidebarProperties: {
        onlyOnPhone: false,
        offsetTop: 80,
      },

      attrs: {
        itemScope: true,
        itemType: 'http://schema.org/VideoGame',
      },
    };
  };

  renderSemanticMeta = (game) => {
    const { personsItems, locale } = this.props;
    const image = game.image || game.background_image;
    const { slug, persons } = game;
    const siteUrl = getSiteUrl(locale);

    return (
      <div>
        {image && <meta itemProp="image" content={image} />}
        <meta itemProp="applicationCategory" content="Game" />
        <meta itemProp="url" content={`${siteUrl}${paths.game(slug)}`} />
        <meta itemProp="discussionUrl" content={`${siteUrl}${paths.gameReviews(slug)}`} />
        <AggregateRating game={game} />
        {persons &&
          Array.isArray(personsItems) &&
          personsItems.length > 0 &&
          personsItems.map((person) => (
            <div key={person.id} itemProp="creator" itemScope itemType="http://schema.org/Person">
              <meta itemProp="name" content={person.name} />
              <meta itemProp="url" content={`${siteUrl}${paths.creator(person.slug)}`} />
              <meta itemProp="image" content={person.image || person.image_background} />
            </div>
          ))}
      </div>
    );
  };

  renderHead = (game) => {
    const { size } = this.props;
    const { name, released, platforms, parent_platforms, playtime, promo } = game;

    return (
      <GameHeadBlock
        size={size}
        name={name}
        released={released}
        platforms={platforms}
        parent_platforms={parent_platforms}
        playtime={playtime}
        promo={promo}
      />
    );
  };

  renderScreenshots = (game) => {
    const { slug, screenshots, movies, name, clip } = game;

    return (
      <GameScreenshotsBlock
        openViewer={this.openViewer}
        screenshots={screenshots}
        movies={movies}
        slug={slug}
        clip={clip}
        name={name}
      />
    );
  };

  renderEditBtn = (game) => {
    const { locale } = this.props;
    const { slug, updated } = game;

    if (locale !== 'en') {
      return null;
    }

    return (
      <>
        <HiddenLink className="game__edit-button" to={paths.gameEditBasic(slug)} rel="nofollow">
          <SVGInline svg={editIcon} />
          <SimpleIntlMessage id="game.edit_btn" />
        </HiddenLink>
        {updated && (
          <div className="game__edit__last-modified-str">
            <FormattedMessage
              id="game.last_modified"
              values={{
                time: <Time date={updated} />,
              }}
            />
          </div>
        )}
      </>
    );
  };

  renderAbout(game) {
    const { locale, isSpider } = this.props;
    const { slug, description, loading, review, editorial_review } = game;
    const descriptionLength = len(game, 'description');

    return loading === false || descriptionLength > 0 ? (
      <GameAboutBlock
        locale={locale}
        gameSlug={slug}
        description={description}
        review={get(review, 'text')}
        editorialReview={editorial_review}
        isSpider={isSpider}
      />
    ) : (
      <ContentPlaceholder rows={10} />
    );
  }

  renderAchievements(game) {
    const { slug, achievements, name } = game;

    return <GameAchievementsBlock slug={slug} achievements={achievements} name={name} />;
  }

  renderAvailability(game) {
    const { dispatch, currentUser } = this.props;
    const { stores } = game;

    if (len(stores) === 0) return null;

    return (
      <GameAvailabilityBlock stores={stores} gameId={game.id} dispatch={dispatch} currentUserId={currentUser.id} />
    );
  }

  renderContributors(game) {
    const { dispatch, currentUser } = this.props;
    const { contributors } = game;

    if (!contributors || len(contributors.results) === 0) return null;

    return (
      <GameContributorsBlock
        contributors={contributors.results}
        gameId={game.id}
        dispatch={dispatch}
        currentUserId={currentUser.id}
      />
    );
  }

  renderButtons(game) {
    const { size, currentUser, dispatch } = this.props;

    return (
      <GameButtonsNew
        className="game__buttons"
        appSize={size}
        game={game}
        currentUser={currentUser}
        dispatch={dispatch}
      />
    );
  }

  renderCharts(game) {
    const { ratings, genres } = this.props;
    const { rating_top, reviews_count = 0, charts } = game;

    return (
      <RatingChart
        ratingTop={rating_top}
        reviewsCount={reviews_count}
        allRatings={ratings}
        charts={charts}
        genres={genres}
      />
    );
  }

  renderCollections(game) {
    const { slug, collections, name } = game;

    return <GameCollectionsBlock slug={slug} collections={collections} name={name} />;
  }

  renderImgur(game) {
    const { slug, imgur, name } = game;

    if (config.features.imgur) {
      return <GameImgurBlock slug={slug} imgur={imgur} name={name} openViewer={this.openViewer} />;
    }

    return null;
  }

  renderFiles(game) {
    const { locale } = this.props;
    const { slug, files_count } = game;
    const { patches: patchesCount = 0, demos: demosCount = 0, cheats: cheatsCount = 0 } = files_count || {};

    return (
      <GameFilesBlock
        locale={locale}
        gameSlug={slug}
        cheatsCount={cheatsCount}
        demosCount={demosCount}
        patchesCount={patchesCount}
      />
    );
  }

  renderMeta(game) {
    return (
      <GameMetaBlock
        platforms={game.platforms}
        metacritic={game.metacritic}
        developers={game.developers}
        genres={game.genres}
        tags={game.tags}
        publishers={game.publishers}
        released={game.released}
        esrb_rating={game.esrb_rating}
        website={game.website}
        tba={game.tba}
        additions={game.additions}
        gameSeries={game.gameSeries}
        parents={game.parents}
      />
    );
  }

  renderOwners(game) {
    const { owners, id, name, slug, user_review, reviews_count } = game;

    return (
      <GameOwnersBlock
        id={id}
        name={name}
        slug={slug}
        owners={owners}
        user_review={user_review}
        reviews_count={reviews_count}
      />
    );
  }

  renderRatings = (game) => {
    const { currentUser, ratings: allRatings, dispatch } = this.props;
    const { id, name, slug, reviews_count, ratings, user_review, reviews } = game;

    return (
      <GameRatingsBlock
        reviews={reviews}
        dispatch={dispatch}
        id={id}
        name={name}
        slug={slug}
        reviews_count={reviews_count}
        user_review={user_review}
        ratings={ratings}
        allRatings={allRatings}
        currentUserId={currentUser.id}
      />
    );
  };

  renderReddit(game) {
    const { size } = this.props;
    const { slug, reddit, name } = game;
    const { results, count } = reddit || {};

    if (len(results) === 0) return null;

    return <RedditList size={size} titleLink={paths.gameReddit(slug)} count={count} results={results} name={name} />;
  }

  renderSuggestions(game) {
    const { dispatch, currentUser, suggestedGames, size, ratings: allRatings } = this.props;
    const { slug, suggestions, name } = game;

    return (
      <GameSuggestionsBlock
        slug={slug}
        name={name}
        suggestions={suggestions}
        dispatch={dispatch}
        currentUser={currentUser}
        suggestedGames={suggestedGames}
        load={this.loadSuggestedGames}
        appSize={size}
        allRatings={allRatings}
      />
    );
  }

  renderSystemRequirements(game) {
    const { platforms } = game;

    return <GameSystemRequirementsBlock platforms={platforms} />;
  }

  renderTabs() {
    const game = getGame(this.props);
    const { tab } = this.state;
    const { ratings, dispatch, intl, size } = this.props;
    const { id, name, slug, reviews, posts, background_image_additional } = game;

    return (
      <RenderMounted>
        {({ visible, onChildReference }) => (
          <div ref={(reference) => onChildReference(reference)} className="game__tabs-content">
            <div
              className="game__tabs-background"
              style={this.getTabsBackground(background_image_additional, visible)}
            />

            <Element name="reviews" />
            <Heading className="game__tabs__title" rank={2}>
              {intl.formatMessage({ id: 'game.reviews_and_comments' }, { name })}
            </Heading>

            <Tabs className="game__tabs" centred={false}>
              {['reviews', 'posts'].map((t) => (
                <Tab
                  className="game__tab"
                  counter={get(game, `[${t}].count`, 0)}
                  active={t === tab}
                  onClick={() => this.showTab(t)}
                  key={t}
                >
                  <FormattedMessage id={`game.${t}`} />
                </Tab>
              ))}
            </Tabs>

            {tab === 'reviews' && (
              <GameReviewsBlock
                appSize={size}
                dispatch={dispatch}
                intl={intl}
                game={game}
                reviews={reviews}
                ratings={ratings}
                compact
              />
            )}

            {tab === 'posts' && (
              <GamePostsBlock dispatch={dispatch} intl={intl} id={id} name={name} slug={slug} posts={posts} />
            )}
          </div>
        )}
      </RenderMounted>
    );
  }

  renderSeoLinks() {
    return (
      <div
        className="game__seo-links"
        dangerouslySetInnerHTML={{
          __html: '<!--# include virtual="/api/seo-links?uri=$uri" -->',
        }}
      />
    );
  }

  renderTwitch(game) {
    const { slug, twitch, name } = game;

    return <GameTwitchBlock slug={slug} name={name} twitch={twitch} openViewer={this.openViewer} />;
  }

  renderViewer() {
    const { viewerVisible, viewerContent, viewerIndex = 0, additionalDataLoaded } = this.state;
    const { GameViewer } = this;

    if (!viewerVisible || !additionalDataLoaded) return null;

    return <GameViewer content={viewerContent} activeIndex={viewerIndex} onClose={this.closeViewer} />;
  }

  renderYoutube(game) {
    const { slug, youtube, youtube_count, name } = game;

    return <GameYoutubeBlock youtube={youtube} youtube_count={youtube_count} slug={slug} name={name} />;
  }

  renderPersons(game) {
    const { size, personsItems, locale } = this.props;
    const { slug, persons, creators_count, name } = game;

    const fullPresons = { ...persons, results: personsItems };

    return (
      <GamePersons
        persons={fullPresons}
        size={size}
        slug={slug}
        creators_count={creators_count}
        name={name}
        appLocale={locale}
      />
    );
  }

  renderFloatingHeader(game) {
    const { dispatch, currentUser, ratings: allRatings, genres } = this.props;
    const { additionalDataLoaded, activeSection } = this.state;

    if (additionalDataLoaded) {
      const { GameHeader } = this;
      return (
        <GameHeader
          game={game}
          dispatch={dispatch}
          activeSection={activeSection}
          currentUser={currentUser}
          allRatings={allRatings}
          genres={genres}
          isMainPage
        />
      );
    }

    return null;
  }

  renderBreadcrumbs(game) {
    const {
      location: { pathname },
    } = this.props;
    const { name, slug } = game;

    return <Breadcrumbs path={pathname} customNames={{ [slug]: name }} />;
  }

  renderDesktopPage() {
    const game = getGame(this.props);

    return (
      <>
        {this.renderFloatingHeader(game)}
        <Page {...this.getPageProps(game)} key={game.id}>
          <div className="game__content-wrap">
            {this.renderBreadcrumbs(game)}
            <div className="game-content-columns">
              <div>
                {this.renderSemanticMeta(game)}
                {this.renderHead(game)}
                {this.renderButtons(game)}
                {this.renderCharts(game)}
                {this.renderRatings(game)}
                {this.renderOwners(game)}
                {this.renderAbout(game)}
                {this.renderFiles(game)}
                {this.renderMeta(game)}
                {this.renderSystemRequirements(game)}
              </div>
              <div>
                {this.renderScreenshots(game)}
                {this.renderEditBtn(game)}
                {this.renderAvailability(game)}
                {this.renderContributors(game)}
                {this.renderCollections(game)}
              </div>
            </div>
            <Content columns="1">
              {this.renderSuggestions(game)}
              {this.renderYoutube(game)}
              {this.renderPersons(game)}
              {this.renderImgur(game)}
              {this.renderAchievements(game)}
              {this.renderReddit(game)}
              {this.renderTwitch(game)}
              {this.renderTabs(game)}
              {this.renderSeoLinks()}
            </Content>
            {this.renderViewer(game)}
          </div>
        </Page>
      </>
    );
  }

  renderPhonePage() {
    const game = getGame(this.props);
    const video = this.getGameVideo(game);
    const pagePropertiesArgument = this.getPageProps(game);

    const pageProperties = {
      ...pagePropertiesArgument,
      art: {
        ...pagePropertiesArgument.art,
        height: video ? '56.25vw' : '300px',
      },
    };

    return (
      <>
        <Page {...pageProperties} key={game.id}>
          {this.renderBreadcrumbs(game)}
          <Content columns="1">
            {this.renderSemanticMeta(game)}
            {this.renderHead(game)}
            {this.renderScreenshots(game)}
            {this.renderButtons(game)}
            {this.renderEditBtn(game)}
            {this.renderCharts(game)}
            {this.renderRatings(game)}
            {this.renderOwners(game)}
            {this.renderAbout(game)}
            {this.renderFiles(game)}
            {this.renderMeta(game)}
            {this.renderSystemRequirements(game)}
            {this.renderAvailability(game)}
            {this.renderContributors(game)}
            {this.renderSuggestions(game)}
            {this.renderCollections(game)}
            {this.renderYoutube(game)}
            {this.renderPersons(game)}
            {this.renderImgur(game)}
            {this.renderAchievements(game)}
            {this.renderReddit(game)}
            {this.renderTwitch(game)}
            {this.renderTabs(game)}
            {this.renderSeoLinks()}
          </Content>
          {this.renderViewer(game)}
        </Page>
      </>
    );
  }

  render() {
    const { size } = this.props;

    return appHelper.isDesktopSize(size) ? this.renderDesktopPage() : this.renderPhonePage();
  }
}
